class CartCrossSell extends HTMLElement {
  connectedCallback() {
    const scrollContainer = this.closest('.drawer__contents') || null;
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      },
      { root: scrollContainer, rootMargin: '0px 0px 150px 0px' }
    );
    this.observer.observe(this);
  }

  formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  resizeImage(url, width) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

  // Product titles here always end with the size in parentheses, e.g.
  // "Vintage Nike Trainingshose (L)". Items without a size (accessories
  // like sunglasses) have no trailing group and are treated as size-agnostic.
  parseTitle(title) {
    const match = (title || '').match(/^(.*?)\s*\(([^()]+)\)\s*$/);
    if (match) {
      return { base: match[1].trim(), size: match[2].trim().toUpperCase() };
    }
    return { base: (title || '').trim(), size: null };
  }

  async fetchRecommendations(productId) {
    try {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
      const data = await res.json();
      return data.products || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  // Availability and the "custom.geschlecht" (gender) metafield aren't exposed
  // by the public /products.json or recommendations endpoints, so this feeds
  // off a small theme template (templates/collection.metafields-feed.liquid)
  // that dumps id/title/availability/price/gender for the whole catalog.
  async fetchFeed() {
    const cacheKey = 'onhowCrossSellFeedV2';
    const cacheTtlMs = 10 * 60 * 1000;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.ts < cacheTtlMs) return parsed.products;
      }
    } catch (error) {
      // sessionStorage unavailable or corrupt cache — refetch below
    }

    let rawProducts = [];
    try {
      const first = await fetch('/collections/all?view=metafields-feed').then((res) => res.json());
      rawProducts = first.products || [];
      const totalPages = first.totalPages || 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map((page) =>
            fetch(`/collections/all?view=metafields-feed&page=${page}`)
              .then((res) => res.json())
              .then((data) => data.products || [])
              .catch(() => [])
          )
        );
        rawProducts = rawProducts.concat(rest.flat());
      }
    } catch (error) {
      console.error(error);
    }

    const products = rawProducts.map((product) => ({
      id: product.id,
      title: product.title,
      available: !!product.available,
      featured_image: product.image,
      gender: product.gender || null,
      variants: [{ id: product.variant_id, price: product.price }],
    }));

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products }));
    } catch (error) {
      // storage full or unavailable — cache is best-effort only
    }

    return products;
  }

  async load() {
    let cart;
    try {
      cart = await fetch('/cart.js').then((response) => response.json());
    } catch (error) {
      return;
    }
    if (!cart.items.length) {
      this.classList.add('cart-cross-sell--empty');
      return;
    }

    const cartProductIds = new Set(cart.items.map((item) => item.product_id));
    const seedIds = [...cartProductIds];

    // Fetch the feed and all per-item recommendations in parallel rather than
    // sequentially — with several cart items this was the main source of the
    // ~1s delay before the tray content appeared.
    const [feed, candidateLists] = await Promise.all([
      this.fetchFeed(),
      Promise.all(seedIds.map((seedId) => this.fetchRecommendations(seedId))),
    ]);
    const genderById = new Map(feed.map((product) => [product.id, product.gender]));

    const cartSizes = new Set();
    const cartGenders = new Set();
    cart.items.forEach((item) => {
      const { size } = this.parseTitle(item.product_title);
      if (size) cartSizes.add(size);
      const gender = genderById.get(item.product_id);
      if (gender === 'Mann' || gender === 'Frau') cartGenders.add(gender);
    });
    const hasSizedCartItems = cartSizes.size > 0;
    const hasGenderedCartItems = cartGenders.size > 0;

    // Unisex items, and items with no gender metafield at all, never rule a
    // candidate out — mirrors how a candidate with no size never gets excluded.
    const genderMatches = (gender) =>
      !hasGenderedCartItems || gender == null || gender === 'Unisex' || cartGenders.has(gender);

    const isEligible = (product) => {
      if (!product.available || cartProductIds.has(product.id)) return false;
      if (!genderMatches(genderById.get(product.id))) return false;
      if (!hasSizedCartItems) return true;
      const { size } = this.parseTitle(product.title);
      return size === null || cartSizes.has(size);
    };

    const seen = new Set();
    const products = [];

    for (const candidates of candidateLists) {
      if (products.length >= 6) break;
      for (const product of candidates) {
        if (products.length >= 6) break;
        if (seen.has(product.id) || !isEligible(product)) continue;
        seen.add(product.id);
        products.push(product);
      }
    }

    // Same-size/-gender candidates from Shopify's own recommendation pool can
    // run out fast since every item here is a one-off piece. When that
    // happens, widen the search to the whole catalog for anything else that
    // still matches the cart's size and gender constraints.
    if (products.length < 6 && (hasSizedCartItems || hasGenderedCartItems)) {
      const fallbackMatches = feed.filter((product) => !seen.has(product.id) && isEligible(product));

      for (let i = fallbackMatches.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [fallbackMatches[i], fallbackMatches[j]] = [fallbackMatches[j], fallbackMatches[i]];
      }

      for (const product of fallbackMatches) {
        if (products.length >= 6) break;
        seen.add(product.id);
        products.push(product);
      }
    }

    if (!products.length) {
      this.classList.add('cart-cross-sell--empty');
      return;
    }

    this.classList.remove('cart-cross-sell--empty');
    this.innerHTML = `
      <p class="cart-cross-sell__heading">Das könnte dir auch gefallen</p>
      <div class="cart-cross-sell__track">
        ${products.map((product) => this.renderCard(product)).join('')}
      </div>
    `;

    this.querySelectorAll('.cart-cross-sell__add').forEach((btn) => {
      btn.addEventListener('click', (event) => this.addProduct(event, btn));
    });
  }

  renderCard(product) {
    const variant = product.variants[0];
    const image = this.resizeImage(product.featured_image, 160);
    const { base, size } = this.parseTitle(product.title);
    const safeBase = (base || '').replace(/"/g, '&quot;');
    return `
      <div class="cart-cross-sell__card">
        <img
          class="cart-cross-sell__image"
          src="${image}"
          alt="${safeBase}"
          loading="lazy"
          width="72"
          height="94"
        >
        <div class="cart-cross-sell__info">
          <p class="cart-cross-sell__title">${base}</p>
          ${size ? `<p class="cart-cross-sell__size">Größe ${size}</p>` : ''}
          <p class="cart-cross-sell__price">${this.formatMoney(variant.price)}</p>
        </div>
        <button type="button" class="cart-cross-sell__add" data-variant-id="${variant.id}">Hinzufügen</button>
      </div>
    `;
  }

  async addProduct(event, btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', btn.dataset.variantId);
      formData.append('quantity', '1');

      if (cartDrawer && cartDrawer.getSectionsToRender) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      window.__cartMutationSeq = (window.__cartMutationSeq || 0) + 1;
      const requestSeq = window.__cartMutationSeq;

      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const json = await res.json();

      if (json.status) {
        btn.classList.add('is-error');
        setTimeout(() => btn.classList.remove('is-error'), 1200);
        return;
      }

      if (cartDrawer && cartDrawer.renderContents && requestSeq === window.__cartMutationSeq) {
        cartDrawer.renderContents(json);
      }
    } catch (error) {
      console.error(error);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }
}

customElements.define('cart-cross-sell', CartCrossSell);
